import { type KeyboardEvent, type ReactNode, useRef } from "react";

import Text from "@/blueprint/components/Text/Text";
import { optionCardClassName } from "@/views/shop/sections/configurator/OptionCard";

export type RadioOption<Id extends string> = {
  id: Id;
  descriptionId: string;
  isDisabled: boolean;
  content: ReactNode;
};

/**
 * The configurators' single-choice cards: the whole card is the radio, the
 * chosen one carries the ring, and the arrow keys move the choice as they do
 * in a native radio group.
 */
export const OptionRadioGroup = <Id extends string>({
  label,
  heading,
  options,
  selectedId,
  onSelect,
}: {
  /** The group's accessible name. */
  label: string;
  /** Shown over the cards; null when the section title already introduces them. */
  heading: string | null;
  options: readonly RadioOption<Id>[];
  selectedId: Id | null;
  onSelect: (id: Id) => void;
}) => {
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const firstEnabledIndex = options.findIndex((option) => !option.isDisabled);

  const selectAndFocus = (index: number) => {
    const option = options[index];
    if (option === undefined) return;

    onSelect(option.id);
    optionRefs.current[index]?.focus();
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (index + 1) % options.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (index - 1 + options.length) % options.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = options.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    selectAndFocus(nextIndex);
  };

  return (
    <div className="flex flex-col gap-xs" role="radiogroup" aria-label={label}>
      {heading === null ? null : (
        <Text variant="paragraph-md" as="p" className="font-semibold">
          {heading}
        </Text>
      )}
      {options.map((option, index) => {
        const isSelected = option.id === selectedId;
        // With nothing chosen, the first live card takes the tab stop.
        const isTabStop =
          selectedId === null ? index === firstEnabledIndex : isSelected;

        return (
          <button
            key={option.id}
            ref={(node) => {
              optionRefs.current[index] = node;
            }}
            type="button"
            role="radio"
            disabled={option.isDisabled}
            aria-checked={isSelected}
            aria-describedby={option.descriptionId}
            tabIndex={isTabStop ? 0 : -1}
            onClick={() => onSelect(option.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={optionCardClassName(isSelected, option.isDisabled)}
          >
            {option.content}
          </button>
        );
      })}
    </div>
  );
};
